import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Box, IconButton, useMediaQuery, Drawer } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Close as CloseIcon, Menu as MenuIcon } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import NotificationsButton from "../Alerts/NotificationsButton";
import { useNotificationsToggle } from "../../hooks/useNotificationsToggle";
import ModalBasicLayout from "./ModalBasicLayout";
import { APP_DROPDOWN_Z_INDEX } from "../../utils/modalLayer";
import ConfigurationPanel from "./ModalCreateWithIA/ConfigurationPanel";
import GeneratedImagesGallery from "./ModalCreateWithIA/GeneratedImagesGallery";
import EmptyState from "./ModalCreateWithIA/EmptyState";
import TrialContentCTAPopup from "../Alerts/TrialContentCTAPopup";
import { useContentModals } from "../../contexts/contentModalsContext";
import useFocusErrorForm from "../../hooks/useFocusErrorForm";
import { getDefaultAccentForLanguage } from "../../utils/options";
import { useMatchCreativeProductOnOpen } from "../../hooks/useMatchCreativeProductOnOpen";
import {
  ALERT_FILL_BRAND_SETUP_DESCRIPTION_FOR_GENERATE_CREATIVES,
  ALERT_FILL_BRAND_SETUP_TITLE,
  BUTTON_COMPLETE_INFORMATION,
  BUTTON_YES_I_SURE,
  BUTTON_GO_BACK,
  ERROR,
  MODAL_CREATE_WITH_IA_ARIA_LABEL,
  MODAL_CREATE_WITH_IA_SAVED_SUCCESSFULLY,
  MODAL_CREATE_WITH_IA_CLOSE_CONFIRMATION_TITLE,
  MODAL_CREATE_WITH_IA_CLOSE_CONFIRMATION_TEXT,
  MODAL_CREATE_WITH_IA_PHYSICAL_NO_REFERENCE_IMAGES_DESCRIPTION,
  WARNING,
  BUTTON_GENERATE_CONTENT,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { getPathDatabaseByRole } from "../../actions/auth";
import {
  startAddContentsToList,
  startGenerateImages,
  startGenerateVideo,
  startGetSubtitlesConfiguration,
} from "../../actions/user";
import {
  checkAndSaveUserOnboardingChecklist,
  checkAndShowNpsForm,
} from "../../actions/adsCampaigns";
import { startTrackHomeFeatureUsage } from "../../actions/homeFeatureActivity";
import { HOME_FEATURE_ID } from "../../data/homeFeatures";
import { setModalOpenState } from "../../actions/ui";
import { ConfirmAlert, SimpleAlert } from "../Alerts/Alerts";
import {
  ALERT_ICON_TYPE_ERROR,
  BUSINESS_OFFERING_PHYSICAL_PRODUCT,
  BUSINESS_OFFERING_SERVICES,
  CHECKLIST_CREATE_CONTENT,
  COUNTRIES,
  getReferenceAdStoragePath,
  IMAGE_FORMATS,
  NPS_RATING_ACTION_GENERATE_9_CONTENTS,
} from "../../utils/constants";
import { useCreditActionCosts } from "../../hooks/useCreditActionCosts";
import { getIsBlockedActions } from "../../actions/getters";
import {
  startGetBrandInformation,
  startSaveBrandLookAndFeel,
  startSaveBrandLogo,
} from "../../actions/adsCampaigns";
import { useNavigate, useSearchParams } from "react-router-dom";
import { validateModalCreateWithIATextFields } from "../../pages/Settings/BrandSetup/utils/blockedBrandsValidation";
import { campaignMediaEntriesFromAISaveItems } from "../../services/adsCampaigns";
import { useAdditionalAssetsSelection } from "./ModalCreateWithIA/hooks/useAdditionalAssetsSelection";
import {
  getRemainingReferenceImageSlots,
  mergeReferenceImageUrls,
} from "../../utils/createWithIAReferenceImages";
import {
  scrollToConfigPanelSection,
  REFERENCE_IMAGES_SECTION_ID,
  REFERENCE_AD_SECTION_ID,
} from "./ModalCreateWithIA/utils/configPanelScroll";

const ModalCreateWithIA = ({
  modalOpen,
  onCloseModal,
  initialValues = {},
  onClosePreviousComponents = () => {},
  doReUpdateInitialValues = false,
  onCallbackCampaignMediaImport = undefined,
  onCallbackAfterSaveToGallery = undefined,
  contentFolderID = null,
}) => {
  const { openGallery, openShare, close } = useContentModals();
  const nestedModalIdsRef = useRef([]);
  const { t } = useTranslationApp();
  const {
    ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
    ADS_CAMPAIGN_GENERATE_VIDEO_ACTION,
  } = useCreditActionCosts();
  const initialMediaType = initialValues.mediaType || "image";
  const defaultAspectRatio =
    initialValues.aspectRatio ||
    (initialMediaType === "image" ? "1:1" : "9:16");

  const { control, handleSubmit, watch, setValue, getValues, formState } =
    useForm({
      defaultValues: {
        mediaType: initialMediaType,
        videoType: initialValues.videoType || "ugc",
        duration: initialValues.duration || 30,
        hasText: initialValues.hasText ?? true,
        hasSubtitles: initialValues.hasSubtitles ?? false,
        hasWatermark: initialValues.hasWatermark ?? false,
        hasLogo: initialValues.hasLogo ?? false,
        aspectRatio: defaultAspectRatio,
        language: initialValues.language || "en",
        accent:
          initialValues.accent ||
          getDefaultAccentForLanguage(initialValues.language || "en"),
        prompt: initialValues.prompt || "",
        imagesUrl: initialValues.imagesUrl || [],
        websiteUrl: initialValues.websiteUrl || "",
        businessLookAndFeel: initialValues.businessLookAndFeel || "",
        platform: initialValues.platform || "default",
        isCarousel: initialValues.isCarousel ?? false,
        numberOfImages: initialValues.numberOfImages ?? 3,
        subtitleOptions: initialValues.subtitleOptions || undefined,
        imageLocation: initialValues.imageLocation || "",
        referenceAdUrl: initialValues.referenceAdUrl || "",
      },
      mode: "onChange",
    });

  useFocusErrorForm(formState);
  const { errors } = formState;

  const [brandInformation, setBrandInformation] = useState({});
  const {
    assets: additionalAssets,
    enabledMap: additionalAssetsEnabledMap,
    activeAssets: activeAdditionalAssets,
    onLoadAssets: onLoadAdditionalAssets,
    onToggleAsset: onToggleAdditionalAsset,
    onRestoreEnabledMapFromSentAssets: onRestoreAdditionalAssetsFromSent,
  } = useAdditionalAssetsSelection();
  const [isGenerating, setIsGenerating] = useState(
    initialValues.isGenerating ?? false
  );
  const [generatedMedia, setGeneratedMedia] = useState(
    initialValues.generatedMedia || []
  );
  const [ratings, setRatings] = useState(initialValues.ratings || {});
  const [activeTab, setActiveTab] = useState(0);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [trialCTAPopupOpen, setTrialCTAPopupOpen] = useState(false);
  const [trialCTACreatives, setTrialCTACreatives] = useState([]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [isConfigOpen, setIsConfigOpen] = useState(true);
  const [shouldExpandAdvancedAccordion, setShouldExpandAdvancedAccordion] =
    useState(false);
  const toggleConfig = () => setIsConfigOpen((prev) => !prev);
  const galleryPanelRef = useRef(null);
  const generatedHighlightTimeoutRef = useRef(null);
  const [highlightedGalleryKeys, setHighlightedGalleryKeys] = useState([]);

  const notifyGeneratedGallerySaved = useCallback((keys = []) => {
    if (!Array.isArray(keys) || keys.length === 0) {
      return;
    }

    setHighlightedGalleryKeys(keys.map(String));
    if (generatedHighlightTimeoutRef.current) {
      clearTimeout(generatedHighlightTimeoutRef.current);
    }
    generatedHighlightTimeoutRef.current = setTimeout(() => {
      setHighlightedGalleryKeys([]);
    }, 5000);

    setTimeout(() => {
      galleryPanelRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }, 50);
  }, []);

  useEffect(() => {
    return () => {
      if (generatedHighlightTimeoutRef.current) {
        clearTimeout(generatedHighlightTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const nestedModalIds = nestedModalIdsRef;
    return () => {
      nestedModalIds.current.forEach((id) => close(id));
      nestedModalIds.current = [];
    };
  }, [close]);

  const focusReferenceImagesSection = useCallback(() => {
    setIsConfigOpen(true);
    scrollToConfigPanelSection({ sectionId: REFERENCE_IMAGES_SECTION_ID });
  }, []);

  const { onToggle: onToggleNotifications, hasActiveProcessing } =
    useNotificationsToggle();

  const dispatch = useDispatchApp();

  const subscriptionPlanInfo = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo
  );
  const isTrial = useSelectorApp(
    (state) => state?.payments?.subscriptionPlanInfo?.isTrial
  );
  const isPlanActive = useSelectorApp((state) => state?.payments?.isPlanActive);

  const [params] = useSearchParams();
  const campaignID = params.get("campaignID");

  useEffect(() => {
    dispatch(setModalOpenState("isModalCreateWithIA", modalOpen));
    return () => {
      dispatch(setModalOpenState("isModalCreateWithIA", false));
    };
  }, [modalOpen, dispatch]);

  useEffect(() => {
    if (modalOpen) {
      setIsConfigOpen(true);
    }
    // eslint-disable-next-line
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen) return;
    if (!initialValues?.referenceAdUrl) return;

    setIsConfigOpen(true);
    scrollToConfigPanelSection({ sectionId: REFERENCE_AD_SECTION_ID });
  }, [modalOpen, initialValues?.referenceAdUrl]);

  useEffect(() => {
    if (!doReUpdateInitialValues) return;

    if (initialValues && Object.keys(initialValues).length > 0) {
      const currentValues = getValues();
      setValue("mediaType", initialValues.mediaType || currentValues.mediaType);
      setValue("videoType", initialValues.videoType || currentValues.videoType);
      setValue("duration", initialValues.duration || currentValues.duration);
      setValue("hasText", initialValues.hasText ?? currentValues.hasText);
      setValue(
        "hasSubtitles",
        initialValues.hasSubtitles ?? currentValues.hasSubtitles
      );
      setValue(
        "hasWatermark",
        initialValues.hasWatermark ?? currentValues.hasWatermark
      );
      setValue("hasLogo", initialValues.hasLogo ?? currentValues.hasLogo);
      setValue(
        "aspectRatio",
        initialValues.aspectRatio || currentValues.aspectRatio
      );
      setValue("language", initialValues.language || currentValues.language);
      setValue(
        "accent",
        initialValues.accent ||
          currentValues.accent ||
          getDefaultAccentForLanguage(
            initialValues.language || currentValues.language || "en"
          )
      );
      setValue("prompt", initialValues.prompt || currentValues.prompt);
      setValue("imagesUrl", initialValues.imagesUrl || currentValues.imagesUrl);
      setValue(
        "websiteUrl",
        initialValues.websiteUrl || currentValues.websiteUrl
      );
      setValue(
        "businessLookAndFeel",
        initialValues.businessLookAndFeel || currentValues.businessLookAndFeel
      );
      setValue(
        "platform",
        initialValues.platform || currentValues.platform || "default"
      );
      setValue(
        "isCarousel",
        initialValues.isCarousel ?? currentValues.isCarousel ?? false
      );
      setValue(
        "numberOfImages",
        initialValues.numberOfImages ?? currentValues.numberOfImages ?? 3
      );
      setValue(
        "subtitleOptions",
        initialValues.subtitleOptions || currentValues.subtitleOptions
      );
      setValue(
        "imageLocation",
        initialValues.imageLocation ?? currentValues.imageLocation ?? ""
      );
      setValue(
        "referenceAdUrl",
        initialValues.referenceAdUrl ?? currentValues.referenceAdUrl ?? ""
      );

      if (initialValues.generatedMedia) {
        setGeneratedMedia(initialValues.generatedMedia);
      }

      if (typeof initialValues.isGenerating === "boolean") {
        setIsGenerating(initialValues.isGenerating);
      }

      if (Array.isArray(initialValues.additionalAssets)) {
        onRestoreAdditionalAssetsFromSent({
          sentAssets: initialValues.additionalAssets,
        });
      }
    }
    //eslint-disable-next-line
  }, [initialValues]);

  useEffect(() => {
    if (!modalOpen) {
      setShouldExpandAdvancedAccordion(false);
    }
  }, [modalOpen]);

  useMatchCreativeProductOnOpen({
    modalOpen,
    getValues,
    setValue,
    campaignID,
    initialValues,
    enabled: !doReUpdateInitialValues,
    onProductMatched: () => setShouldExpandAdvancedAccordion(true),
  });

  const imagesUrl = watch("imagesUrl") || [];
  const mediaType = watch("mediaType");
  const aspectRatio = watch("aspectRatio");
  const numberOfImages = watch("numberOfImages");

  useEffect(() => {
    if (!initialValues.aspectRatio) {
      const defaultAspectRatio =
        mediaType === "image" || mediaType === "carousel" ? "1:1" : "9:16";
      setValue("aspectRatio", defaultAspectRatio);
    }
    // eslint-disable-next-line
  }, [mediaType]);

  const navigate = useNavigate();
  const { userID } = dispatch(getPathDatabaseByRole());
  const hasReferenceImage = imagesUrl.length > 0;

  const hasBrandContext = Boolean(
    brandInformation.businessName && brandInformation.businessDescription
  );
  const getBrandInformation = async () => {
    const response = await dispatch(startGetBrandInformation());
    if (response.ok) {
      const { data } = response;
      setBrandInformation(data);
      setValue("businessLookAndFeel", data?.businessLookAndFeel || "");
      setValue("imageLocation", data?.businessCountry || "");
      onLoadAdditionalAssets({
        assets:
          data?.hasAdditionalAssets === true ? data?.additionalAssets : [],
        ...(data?.hasAdditionalAssets === true &&
          Array.isArray(initialValues?.additionalAssets) && {
            sentAssets: initialValues.additionalAssets,
          }),
      });
    }
  };
  const getSubtitlesConfiguration = async () => {
    const data = await dispatch(startGetSubtitlesConfiguration());
    if (data) {
      setValue("subtitleOptions", data);
    }
  };

  const onSaveLogo = async (logoUrl) => {
    try {
      await dispatch(startSaveBrandLogo(logoUrl));
      setBrandInformation((prev) => ({ ...prev, logoUrl }));
    } catch (e) {
      console.log(e);
    }
  };

  const onSubmit = async () => {
    const data = getValues();
    const imagesUrl = data.imagesUrl || [];
    try {
      validateModalCreateWithIATextFields({
        formData: {
          prompt: data.prompt,
          businessLookAndFeel: data.businessLookAndFeel,
          websiteUrl: data.websiteUrl,
          referenceAdUrl: data.referenceAdUrl,
        },
        isPlanActive,
        isTrial,
      });
    } catch (error) {
      SimpleAlert({
        title: t(ERROR),
        text: error?.message || "The request could not be processed.",
        icon: ALERT_ICON_TYPE_ERROR,
      });
      return;
    }

    const isPhysicalBrand =
      brandInformation?.businessOffering === BUSINESS_OFFERING_PHYSICAL_PRODUCT;
    const isServiceBrand =
      brandInformation?.businessOffering === BUSINESS_OFFERING_SERVICES;

    const actualMediaTypeEarly =
      data.mediaType === "carousel" ? "image" : data.mediaType;
    const isServiceVideo = isServiceBrand && actualMediaTypeEarly === "video";

    if (isPhysicalBrand && imagesUrl.length === 0) {
      const physicalReferenceAlert = await ConfirmAlert({
        title: t(WARNING),
        text: t(MODAL_CREATE_WITH_IA_PHYSICAL_NO_REFERENCE_IMAGES_DESCRIPTION),
        confirmButtonText: t(BUTTON_GO_BACK),
        cancelButtonText: t(BUTTON_GENERATE_CONTENT),
        showCancelButton: true,
        icon: "warning",
      });

      if (physicalReferenceAlert.isConfirmed) {
        focusReferenceImagesSection();
        return;
      }

      const didChooseGenerate =
        physicalReferenceAlert.isDismissed &&
        physicalReferenceAlert.dismiss === "cancel";

      if (!didChooseGenerate) {
        return;
      }
    }

    try {
      const currentBLF = getValues("businessLookAndFeel") ?? "";
      dispatch(startSaveBrandLookAndFeel(currentBLF));
    } catch (e) {
      console.log(e);
    }

    const actualMediaType =
      data.mediaType === "carousel" ? "image" : data.mediaType;
    const isCarouselMode = data.mediaType === "carousel";
    const isVideo = actualMediaType === "video";

    let parsedNumberOfImages = parseInt(data.numberOfImages, 10);
    if (isNaN(parsedNumberOfImages)) {
      parsedNumberOfImages = 3;
    }
    if (parsedNumberOfImages < 1) {
      parsedNumberOfImages = 1;
    }
    if (parsedNumberOfImages > 10) {
      parsedNumberOfImages = 10;
    }

    let multiplier;
    if (isVideo) {
      multiplier = 1;
    } else {
      multiplier = parsedNumberOfImages || 1;
    }

    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: isVideo
          ? ADS_CAMPAIGN_GENERATE_VIDEO_ACTION
          : ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
        showUsageConfirmationAlert: true,
        multiplier: multiplier,
      })
    );
    if (isBlocked) {
      return;
    }

    if (!hasBrandContext) {
      const response = await ConfirmAlert({
        title: t(ALERT_FILL_BRAND_SETUP_TITLE),
        text: t(ALERT_FILL_BRAND_SETUP_DESCRIPTION_FOR_GENERATE_CREATIVES),
        showCancelButton: false,
        confirmButtonText: t(BUTTON_COMPLETE_INFORMATION),
      });
      if (response.isConfirmed) {
        onCloseModal();
        onClosePreviousComponents();
        navigate("/settings");
        return;
      }

      return;
    }

    setIsGenerating(true);

    try {
      const countryOption = data.imageLocation
        ? COUNTRIES.find((c) => c.value === data.imageLocation)
        : null;
      const locationName = countryOption ? t(countryOption.label) : undefined;

      const generateParams = {
        prompt: data.prompt,
        language: data.hasText ? data.language : undefined,
        ...(actualMediaType === "video" && {
          accent: data.accent,
          hasWatermark: data.hasWatermark,
        }),
        ...(actualMediaType === "image" &&
          locationName && { location: locationName }),
        size: data.dimensions,
        websiteUrl: data.websiteUrl,
        imagesUrl: mergeReferenceImageUrls({
          currentUrls: [],
          newUrls: data.imagesUrl || [],
          mediaType: data.mediaType,
        }),
        mediaType: actualMediaType,
        hasText: data.hasText,
        hasSubtitles: data.hasSubtitles,
        ...(data.hasSubtitles &&
          data.subtitleOptions && {
            subtitleOptions: data.subtitleOptions,
          }),
        logoUrl:
          (isServiceVideo || data.hasLogo) && brandInformation?.logoUrl
            ? brandInformation.logoUrl
            : undefined,
        campaignID,
        campaignContext: initialValues.campaignContext,
        aspectRatio: data.aspectRatio,
        videoType: data.videoType,
        duration: data.duration,
        generatedMedia,
        ratings,
        platform: data.platform,
        ...(actualMediaType === "image" && {
          isCarousel: isCarouselMode,
          numberOfImages: parsedNumberOfImages,
          ...(data.referenceAdUrl && {
            referenceAdUrl: data.referenceAdUrl,
          }),
        }),
        ...(actualMediaType !== "video" &&
          brandInformation?.hasAdditionalAssets === true &&
          activeAdditionalAssets.length > 0 && {
            additionalAssets: activeAdditionalAssets,
          }),
      };

      setIsGenerating(true);
      const response = await dispatch(
        actualMediaType === "video"
          ? startGenerateVideo(generateParams)
          : startGenerateImages(generateParams)
      );
      if (response.ok) {
        const responseMedia =
          response.data.images || response.data.videos || [];
        let media = [];
        if (actualMediaType === "video") {
          media = responseMedia;
        } else {
          const newRatings = {};
          const responseCarouselID = response.data.carouselID;

          if (responseCarouselID) {
            const carouselUrls = responseMedia.map((image) => {
              newRatings[image.url] = image.rating;
              return image.url;
            });

            media = [
              {
                type: "carousel",
                carouselID: responseCarouselID,
                urls: carouselUrls,
                rating: null,
              },
            ];
          } else {
            media = responseMedia.map((image) => {
              newRatings[image.url] = image.rating;
              if (image.carouselID) {
                return {
                  url: image.url,
                  carouselID: image.carouselID,
                  rating: image.rating,
                };
              }
              return image.url;
            });
          }
          setRatings((prev) => ({ ...prev, ...newRatings }));
        }
        setGeneratedMedia((prev) => [...media, ...prev]);

        // Calculate the total number of used contents (creatives + videos)
        const usedCreatives =
          (subscriptionPlanInfo?.benefits?.creatives || 0) -
          (subscriptionPlanInfo?.usage?.creatives || 0);
        const usedVideos =
          (subscriptionPlanInfo?.benefits?.videos || 0) -
          (subscriptionPlanInfo?.usage?.videos || 0);
        const totalUsedContent = usedCreatives + usedVideos;

        if (totalUsedContent >= 9) {
          dispatch(checkAndShowNpsForm(NPS_RATING_ACTION_GENERATE_9_CONTENTS));
        }
        dispatch(checkAndSaveUserOnboardingChecklist(CHECKLIST_CREATE_CONTENT));
        dispatch(
          startTrackHomeFeatureUsage({ featureID: HOME_FEATURE_ID.CONTENT })
        );
      } else {
        const hasMessage = response.message;
        if (hasMessage) {
          SimpleAlert({
            title: t(ERROR),
            text: response.message,
            icon: ALERT_ICON_TYPE_ERROR,
          });
        }
      }
    } catch (error) {
      console.log(error);
    }

    setIsGenerating(false);
    return;
  };

  const onGallerySelection = (selectedItems, target) => {
    if (selectedItems && selectedItems.length > 0) {
      const imageUrls = selectedItems.map((item) => item.url || item.src);

      if (target === "referenceAd") {
        const firstUrl = imageUrls[0];
        if (firstUrl) {
          setValue("referenceAdUrl", firstUrl);
        }
        setIsGalleryOpen(false);
        return;
      }

      const currentUrls = getValues("imagesUrl") || [];
      setValue(
        "imagesUrl",
        mergeReferenceImageUrls({
          currentUrls,
          newUrls: imageUrls,
          mediaType: getValues("mediaType"),
        })
      );
    }

    setIsGalleryOpen(false);
  };

  const openReferenceGallery = (target) => {
    if (isGalleryOpen) {
      return;
    }

    const isReferenceAd = target === "referenceAd";
    setIsGalleryOpen(true);
    const galleryId = openGallery({
      skipAutoSelectOnUpload: true,
      onCallbackSelectedItems: (selectedItems) =>
        onGallerySelection(selectedItems, target),
      multiple: !isReferenceAd,
      showAdAccountOption: true,
      showSocialPostsOption: true,
      showUploadDeviceOption: true,
      pathStorage: isReferenceAd
        ? getReferenceAdStoragePath(userID)
        : `socialMedia/${userID}/`,
      acceptedFormats: IMAGE_FORMATS,
      showButtonCreateWithIA: false,
      maxSelections: isReferenceAd
        ? 1
        : getRemainingReferenceImageSlots({
            currentCount: imagesUrl.length,
            mediaType,
          }),
      onCloseModal: () => {
        setIsGalleryOpen(false);
        nestedModalIdsRef.current = nestedModalIdsRef.current.filter(
          (id) => id !== galleryId
        );
      },
    });
    nestedModalIdsRef.current.push(galleryId);
  };

  const onOpenGallery = () => {
    openReferenceGallery("referenceImages");
  };

  const onOpenGalleryForReferenceAd = () => {
    openReferenceGallery("referenceAd");
  };

  const onSelectMediaToggle = (index) => {
    const mediaItem = generatedMedia[index];
    if (!mediaItem) return;

    // Caso: Objeto agrupado (type: carousel)
    if (mediaItem.type === "carousel" && Array.isArray(mediaItem.urls)) {
      const carouselUrls = mediaItem.urls;
      // Verificar si todas las imágenes del carousel están seleccionadas
      const allSelected = carouselUrls.every((u) => selectedImages.includes(u));

      setSelectedImages((prev) => {
        if (allSelected) {
          // Deseleccionar todas
          return prev.filter((u) => !carouselUrls.includes(u));
        } else {
          // Seleccionar todas (agregando las que falten)
          const newUrls = carouselUrls.filter((u) => !prev.includes(u));
          return [...prev, ...newUrls];
        }
      });
      return;
    }

    const url = typeof mediaItem === "string" ? mediaItem : mediaItem.url;
    if (!url) return;

    // Verificar si esta imagen pertenece a un carousel (Lógica Legacy)
    const carouselID =
      typeof mediaItem === "object" && mediaItem.carouselID
        ? mediaItem.carouselID
        : null;

    if (carouselID) {
      // Si pertenece a un carousel, seleccionar/deseleccionar todas las imágenes del carousel
      const carouselImages = generatedMedia.filter((media) => {
        const mediaCarouselID =
          typeof media === "object" && media.carouselID
            ? media.carouselID
            : null;
        return mediaCarouselID === carouselID;
      });

      const carouselUrls = carouselImages
        .map((media) => (typeof media === "string" ? media : media.url))
        .filter(Boolean);

      // Verificar si todas las imágenes del carousel están seleccionadas
      const allSelected = carouselUrls.every((u) => selectedImages.includes(u));

      setSelectedImages((prev) => {
        if (allSelected) {
          // Deseleccionar todas las imágenes del carousel
          return prev.filter((u) => !carouselUrls.includes(u));
        } else {
          // Seleccionar todas las imágenes del carousel
          const newUrls = carouselUrls.filter((u) => !prev.includes(u));
          return [...prev, ...newUrls];
        }
      });
    } else {
      // Imagen individual
      setSelectedImages((prev) => {
        if (prev.includes(url)) {
          return prev.filter((u) => u !== url);
        } else {
          return [...prev, url];
        }
      });
    }
  };

  const onImportSelectedMediaToGallery = async (selectedMediaUrls) => {
    if (!selectedMediaUrls || selectedMediaUrls.length === 0) {
      return false;
    }

    const selectedUrlSet = new Set(selectedMediaUrls);
    const itemsToUpload = [];
    const processedCarouselIds = new Set();

    const getLegacySelectedUrlsInModalOrder = ({ legacyCarouselId }) => {
      const urls = [];
      generatedMedia.forEach((media) => {
        if (
          typeof media === "object" &&
          (media.status || media.resizingFormat)
        ) {
          return;
        }
        const url = typeof media === "string" ? media : media?.url;
        const carouselID =
          typeof media === "object" && media.carouselID
            ? media.carouselID
            : null;
        if (carouselID === legacyCarouselId && url && selectedUrlSet.has(url)) {
          urls.push(url);
        }
      });
      return urls;
    };

    generatedMedia.forEach((media) => {
      if (typeof media === "object" && (media.status || media.resizingFormat)) {
        return;
      }

      if (media.type === "carousel" && Array.isArray(media.urls)) {
        const carouselID = media.carouselID;
        const hasSelectedUrl = media.urls.some((url) =>
          selectedUrlSet.has(url)
        );

        if (hasSelectedUrl && !processedCarouselIds.has(carouselID)) {
          const carouselRatings = media.urls.map(
            (carouselUrl) => ratings?.[carouselUrl] ?? null
          );
          itemsToUpload.push({
            type: "carousel",
            urls: media.urls,
            ratings: carouselRatings,
            isAIGenerated: true,
          });
          processedCarouselIds.add(carouselID);
        }
        return;
      }

      const url = typeof media === "string" ? media : media?.url;
      if (!url || !selectedUrlSet.has(url)) return;

      const legacyCarouselId =
        typeof media === "object" && media.carouselID ? media.carouselID : null;

      if (legacyCarouselId) {
        if (processedCarouselIds.has(legacyCarouselId)) return;

        const legacyUrls = getLegacySelectedUrlsInModalOrder({
          legacyCarouselId,
        });
        if (legacyUrls.length === 0) return;

        const carouselRatings = legacyUrls.map(
          (legacyUrl) => ratings?.[legacyUrl] ?? null
        );
        itemsToUpload.push({
          type: "carousel",
          urls: legacyUrls,
          ratings: carouselRatings,
          isAIGenerated: true,
        });
        processedCarouselIds.add(legacyCarouselId);
        return;
      }

      itemsToUpload.push({
        url,
        type: "upload",
        rating: ratings?.[url] ?? null,
        isAIGenerated: true,
      });
    });

    if (itemsToUpload.length === 0) {
      return false;
    }

    if (onCallbackCampaignMediaImport) {
      const campaignEntries =
        campaignMediaEntriesFromAISaveItems(itemsToUpload);
      if (campaignEntries.length > 0) {
        onCallbackCampaignMediaImport({ entries: campaignEntries });
      }
    }

    const itemsToSave = contentFolderID
      ? itemsToUpload.map((item) => ({
          ...item,
          folderID: contentFolderID,
        }))
      : itemsToUpload;
    const saveResult = await dispatch(
      startAddContentsToList(itemsToSave, { preserveOrder: true })
    );

    if (!saveResult) {
      return false;
    }

    SimpleAlert({
      title: t(MODAL_CREATE_WITH_IA_SAVED_SUCCESSFULLY),
      icon: "success",
    });

    onCloseModal();

    if (onCallbackAfterSaveToGallery && !onCallbackCampaignMediaImport) {
      onCallbackAfterSaveToGallery();
    }

    return true;
  };

  const onCallbackEditItem = async (
    savePayload,
    itemEditingIndex,
    editData = {}
  ) => {
    const editedUrl =
      typeof savePayload === "string" ? savePayload : savePayload?.url;
    const mode =
      typeof savePayload === "string"
        ? "replace"
        : savePayload?.mode || "replace";

    if (!editedUrl) return;

    if (mode === "new") {
      setGeneratedMedia((prev) => [editedUrl, ...prev]);
      notifyGeneratedGallerySaved([editedUrl]);
      return;
    }

    const oldMediaItem = generatedMedia[itemEditingIndex];

    const isCarouselEdit =
      editData?.isCarousel && editData?.carouselIndex !== undefined;
    const carouselIndex = editData?.carouselIndex;

    if (
      isCarouselEdit &&
      oldMediaItem?.type === "carousel" &&
      Array.isArray(oldMediaItem.urls)
    ) {
      const oldUrl = oldMediaItem.urls[carouselIndex];
      const carouselID = oldMediaItem.carouselID;

      setGeneratedMedia((prev) => {
        const updatedCarousel = prev.map((item) => {
          if (
            item.type === "carousel" &&
            Array.isArray(item.urls) &&
            item.carouselID === carouselID
          ) {
            const updatedUrls = [...item.urls];
            if (updatedUrls[carouselIndex] !== undefined) {
              updatedUrls[carouselIndex] = editedUrl;
            }
            return {
              ...item,
              urls: updatedUrls,
            };
          }
          return item;
        });

        const carouselItemIndex = updatedCarousel.findIndex(
          (item) =>
            item.type === "carousel" &&
            Array.isArray(item.urls) &&
            item.carouselID === carouselID
        );

        if (carouselItemIndex <= 0) {
          return updatedCarousel;
        }

        const carouselItem = updatedCarousel[carouselItemIndex];
        const rest = updatedCarousel.filter(
          (_, index) => index !== carouselItemIndex
        );
        return [carouselItem, ...rest];
      });

      if (oldUrl && selectedImages.includes(oldUrl)) {
        setSelectedImages((prev) =>
          prev.map((url) => (url === oldUrl ? editedUrl : url))
        );
      }

      notifyGeneratedGallerySaved([
        carouselID ? `carousel-${carouselID}` : editedUrl,
      ]);
      return;
    }

    const oldUrl =
      typeof oldMediaItem === "string" ? oldMediaItem : oldMediaItem?.url;

    setGeneratedMedia((prev) => {
      const updatedItem =
        typeof oldMediaItem === "string"
          ? editedUrl
          : { ...oldMediaItem, url: editedUrl };
      const rest = prev.filter((_, index) => index !== itemEditingIndex);
      return [updatedItem, ...rest];
    });

    if (oldUrl && selectedImages.includes(oldUrl)) {
      setSelectedImages((prev) =>
        prev.map((url) => (url === oldUrl ? editedUrl : url))
      );
    }

    notifyGeneratedGallerySaved([editedUrl]);
  };

  const onSelectAllMedia = () => {
    const allMediaUrls = [];

    generatedMedia.forEach((media) => {
      if (media.type === "carousel" && Array.isArray(media.urls)) {
        allMediaUrls.push(...media.urls);
      } else if (typeof media === "string") {
        allMediaUrls.push(media);
      } else if (
        typeof media === "object" &&
        media.url &&
        !media.status &&
        !media.resizingFormat
      ) {
        allMediaUrls.push(media.url);
      }
    });

    const allSelected =
      allMediaUrls.length > 0 &&
      allMediaUrls.every((url) => selectedImages.includes(url));

    if (allSelected) {
      setSelectedImages([]);
    } else {
      setSelectedImages(allMediaUrls);
    }
  };

  const onTrialExpandContent = (item) => {
    setTrialCTACreatives([item]);
    setTrialCTAPopupOpen(true);
  };

  const onTrialDownloadContent = (urls = []) => {
    setTrialCTACreatives(urls);
    setTrialCTAPopupOpen(true);
  };

  const onTrialCreatePost = (item) => {
    setTrialCTAPopupOpen(false);
    if (item) {
      const shareId = openShare({
        item,
        onCloseModal: () => {
          nestedModalIdsRef.current = nestedModalIdsRef.current.filter(
            (id) => id !== shareId
          );
        },
      });
      nestedModalIdsRef.current.push(shareId);
    }
  };

  const onClearTrialContentCTAPopup = () => {
    setTrialCTAPopupOpen(false);
  };

  const onDirectClose = async () => {
    if (generatedMedia.length > 0) {
      const response = await ConfirmAlert({
        title: t(MODAL_CREATE_WITH_IA_CLOSE_CONFIRMATION_TITLE),
        text: t(MODAL_CREATE_WITH_IA_CLOSE_CONFIRMATION_TEXT),
        showCancelButton: true,
        confirmButtonText: t(BUTTON_YES_I_SURE),
        cancelButtonText: t(BUTTON_GO_BACK),
      });
      if (response.isConfirmed) {
        onClearTrialContentCTAPopup();
        onCloseModal();
      }
    } else {
      onClearTrialContentCTAPopup();
      onCloseModal();
    }
  };

  useEffect(() => {
    getBrandInformation();
    getSubtitlesConfiguration();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <ModalBasicLayout
        modalOpen={modalOpen}
        onCloseModal={onDirectClose}
        fullWidth={true}
        fullScreen
        scroll="paper"
        aria-label={t(MODAL_CREATE_WITH_IA_ARIA_LABEL)}
        hasTitle={false}
        sxContent={{
          py: 0,
          px: 0,
          display: "flex",
          flexDirection: "row",
          height: "100vh",
          maxHeight: "100vh",
          overflow: "hidden",
        }}
      >
        <ResponsiveConfigContainer
          isSmallScreen={isSmallScreen}
          open={isConfigOpen}
          onClose={toggleConfig}
        >
          {isConfigOpen && (
            <ConfigurationPanel
              control={control}
              errors={errors}
              setValue={setValue}
              getValues={getValues}
              watch={watch}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              isGenerating={isGenerating}
              onSubmit={handleSubmit(onSubmit)}
              hasReferenceImage={hasReferenceImage}
              onOpenGallery={onOpenGallery}
              onOpenGalleryForReferenceAd={onOpenGalleryForReferenceAd}
              isGalleryOpen={isGalleryOpen}
              onToggleConfig={toggleConfig}
              businessOffering={brandInformation?.businessOffering}
              mediaType={mediaType}
              logoUrl={brandInformation?.logoUrl}
              onChangeBrandInformation={setBrandInformation}
              userID={userID}
              onSaveLogo={onSaveLogo}
              defaultAdvancedAccordionExpanded={
                Boolean(initialValues.expandAdvancedConfigurationAccordion) ||
                shouldExpandAdvancedAccordion
              }
              additionalAssets={additionalAssets}
              additionalAssetsEnabledMap={additionalAssetsEnabledMap}
              onToggleAdditionalAsset={onToggleAdditionalAsset}
              brandInformation={brandInformation}
            />
          )}
        </ResponsiveConfigContainer>

        <Box
          ref={galleryPanelRef}
          sx={{
            flex: 1,
            p: { xs: 2, sm: 4 },
            pt: { xs: 6, sm: 6 },
            overflowY: "auto",
            position: "relative",
            height: "100vh",
            maxHeight: "100vh",
          }}
        >
          {!isConfigOpen && (
            <IconButton
              onClick={toggleConfig}
              sx={{
                position: "absolute",
                top: 15,
                left: 10,
                zIndex: 10,
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box
            sx={{
              position: "absolute",
              top: 15,
              right: 50,
              zIndex: 10,
            }}
          >
            <NotificationsButton
              id="notifications-toggle-button"
              onClick={onToggleNotifications}
              hasActiveProcessing={hasActiveProcessing}
              disabled={false}
              sx={{ mr: 0, height: 40 }}
            />
          </Box>

          <IconButton
            onClick={onDirectClose}
            sx={{
              position: "absolute",
              top: 15,
              right: 6,
            }}
          >
            <CloseIcon />
          </IconButton>

          {isGenerating || generatedMedia.length > 0 ? (
            <GeneratedImagesGallery
              isGenerating={isGenerating}
              ratings={ratings}
              generatedMedia={generatedMedia}
              highlightedIds={highlightedGalleryKeys}
              onChangeGeneratedMedia={setGeneratedMedia}
              onGenerateMore={onSubmit}
              onSelectMedia={onSelectMediaToggle}
              onImportToGallery={onImportSelectedMediaToGallery}
              onSelectAllMedia={onSelectAllMedia}
              selectedImages={selectedImages}
              multiple={true}
              onCallbackEditItem={onCallbackEditItem}
              mediaType={mediaType}
              aspectRatio={aspectRatio}
              numberOfImages={numberOfImages}
              isTrial={isTrial}
              onTrialExpandContent={onTrialExpandContent}
              onTrialDownloadContent={onTrialDownloadContent}
              onClearTrialContentCTAPopup={onClearTrialContentCTAPopup}
            />
          ) : (
            <EmptyState />
          )}
        </Box>
        {isTrial && trialCTAPopupOpen && (
          <TrialContentCTAPopup
            modalOpen={trialCTAPopupOpen}
            onCloseModal={onClearTrialContentCTAPopup}
            creatives={trialCTACreatives}
            onCreatePost={onTrialCreatePost}
          />
        )}
      </ModalBasicLayout>
    </>
  );
};

export default ModalCreateWithIA;

// Internal container for responsive config panel rendering
const ResponsiveConfigContainer = ({
  isSmallScreen,
  open,
  onClose,
  children,
}) => {
  if (isSmallScreen) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        variant="temporary"
        anchor="left"
        ModalProps={{
          keepMounted: true,
          disablePortal: true,
        }}
        PaperProps={{
          sx: {
            width: 350,
            maxWidth: "90vw",
            height: "100%",
            zIndex: APP_DROPDOWN_Z_INDEX,
          },
        }}
      >
        {children}
      </Drawer>
    );
  }

  return children;
};
